// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type {
  LinkedLiquidityGate,
  LinkedLiquiditySectionApi,
} from '@client/banks/components/shared/linked-liquidity-section';
import { type DebtFormValues } from '@client/banks/hooks/use-debt-form';
import {
  BankDebtAmortizationStatus,
  BankDebtProductType,
} from '@client/banks/types/debt';
import { createEmptyDebtAmortizationRow } from '@client/banks/utils/bank-form-defaults';
import { getBankCurrencySymbol } from '@client/banks/utils/bank-currency';
import dayjs from 'dayjs';
import { type MutableRefObject, useCallback, useEffect, useMemo } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type DebtFormOption = {
  value: string;
  label: string;
};

export type DebtFormProps = {
  formId: string;
  onSubmit: (data: DebtFormValues) => void | Promise<void>;
  currencyOptions: readonly DebtFormOption[];
  accountIbanOptions: readonly DebtFormOption[];
  defaultCurrency: string;
  linkedLiquidityApiRef?: MutableRefObject<LinkedLiquiditySectionApi | null>;
  onLinkedLiquidityGateChange?: (gate: LinkedLiquidityGate) => void;
};

export const useDebtForm = ({
  currencyOptions,
  accountIbanOptions,
  defaultCurrency,
  linkedLiquidityApiRef,
  onLinkedLiquidityGateChange,
  ..._
}: DebtFormProps) => {
  const { control, handleSubmit } = useFormContext<DebtFormValues>();

  const { t } = useTranslation();

  const amortizationsFieldArray = useFieldArray({
    control,
    name: 'amortizations',
  });

  const currency = useWatch({ control, name: 'currency' });
  const productType = useWatch({ control, name: 'productType' });

  const isLoan = productType === BankDebtProductType.Loan;
  const isCreditLine = productType === BankDebtProductType.CreditLine;

  useEffect(() => {
    if (isCreditLine && amortizationsFieldArray.fields.length > 0) {
      amortizationsFieldArray.replace([]);
    }
  }, [amortizationsFieldArray, isCreditLine]);

  const currencyPrefix = useMemo(() => getBankCurrencySymbol(currency), [currency]);

  const productTypeOptions = useMemo(
    () =>
      Object.values(BankDebtProductType).map(type => ({
        value: type,
        label: t(`aggregator.create-bank.debts.product-type.${type}`, {
          ns: 'aggregator',
        }),
      })),
    [t],
  );

  const amortizationStatusOptions = useMemo(
    () =>
      Object.values(BankDebtAmortizationStatus).map(status => ({
        value: status,
        label: t(`aggregator.create-bank.debts.amortization-status.${status}`, {
          ns: 'aggregator',
        }),
      })),
    [t],
  );

  const todayMaxDate = useMemo(() => dayjs().format('YYYY-MM-DD'), []);

  const onAddAmortization = useCallback(() => {
    amortizationsFieldArray.prepend(createEmptyDebtAmortizationRow());
  }, [amortizationsFieldArray]);

  return {
    control,
    handleSubmit,
    t,
    currencyPrefix,
    amortizationsFieldArray,
    productTypeOptions,
    amortizationStatusOptions,
    todayMaxDate,
    isLoan,
    isCreditLine,
    onAddAmortization,
    currencyOptions,
    accountIbanOptions,
    defaultCurrency,
    linkedLiquidityApiRef,
    onLinkedLiquidityGateChange,
  };
};
